'use client';

import { LoginWithRedirectOptions, useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { isExplicitInviteEntryUrl } from '@modules/auth/utils/invite-utils';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { markAppEntry } from '@modules/shared/analytics/scripts/fullstoryUtils';
import { AbsoluteSpinner } from 'app/_components/AbsoluteSpinner';

import React, { useEffect } from 'react';
import { IdentityProvider } from 'shared/src/util';

/**
 * sessionStorage key used to track whether a silent auth attempt (`prompt=none`)
 * has already been tried for this tab. Prevents infinite redirect loops when
 * Auth0 has no active session — the first attempt returns `login_required`,
 * and the flag ensures we fall through to an interactive login on the next try.
 */
export const SILENT_AUTH_ATTEMPTED_KEY = 'SILENT_AUTH_ATTEMPTED';

/**
 * sessionStorage key that stores the original page URL before the silent auth
 * redirect. When `prompt=none` fails with `login_required`, the callback page
 * navigates back to this URL so AuthGuard can re-extract context params
 * (connection, login_hint, etc.) and pass them to the interactive login.
 */
export const SILENT_AUTH_RETURN_TO_KEY = 'SILENT_AUTH_RETURN_TO';

const isAuth0Environment = (): boolean =>
  !process.env.AUTH_CLIENT_PROVIDER || process.env.AUTH_CLIENT_PROVIDER === IdentityProvider.AUTH0;

/**
 * OIDC `appState.returnTo` passed through `/callback`. Use `/` on the bare app root
 * so callback treats it like `/` (not an explicit deep link) and runs membership-based
 * routing (single-org shortcut, `/organizations`). Non-root URLs keep `href` so query
 * strings and paths are preserved.
 */
const getOidcStateReturnTo = (): string => {
  const { pathname, search } = window.location;
  const isRootPath = pathname === '/' || pathname === '';

  if (isRootPath && search === '') {
    return '/';
  }

  return window.location.toString();
};

export const AuthGuard: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, user, isLoading, login, loginWithRedirect } = useCommonAuthProvider();
  const [isRedirecting, setRedirecting] = React.useState(false);
  const { cloudMspEnableUiFeatures } = useFlagsProxy();

  // Fallback for users who deep-link directly into an authenticated route,
  // bypassing page.tsx. The sessionStorage gate in markAppEntry ensures this
  // fires at most once per session even if both page.tsx and AuthGuard are mounted.
  useEffect(() => {
    if (isLoading) {
      return;
    }

    markAppEntry(isAuthenticated);
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    if (isLoading || isRedirecting) {
      return;
    }

    if (!isAuthenticated) {
      setRedirecting(true);

      const loginParams: LoginWithRedirectOptions = { returnTo: getOidcStateReturnTo() };

      if (cloudMspEnableUiFeatures) {
        const params = new URLSearchParams(window.location.search);
        const connection = params.get('connection');

        if (connection) {
          loginParams.connection = connection;
        }

        const loginHint = params.get('login_hint');

        if (loginHint) {
          loginParams.loginHint = loginHint;
        }
      }

      // For Auth0 environments, attempt a silent auth check (prompt=none) before
      // falling through to interactive login. This replicates the Auth0 SDK's
      // checkSession() behavior for cross-app SSO (e.g. Leader → Maestro).
      // If the user has an active Auth0 session, they get tokens without seeing
      // a login form. If not, the callback receives `login_required` and clears
      // the flag so the next attempt uses interactive login.
      const silentAuthAttempted = sessionStorage.getItem(SILENT_AUTH_ATTEMPTED_KEY);
      const shouldAttemptSilentAuth = isAuth0Environment() &&
        !silentAuthAttempted &&
        !isExplicitInviteEntryUrl(window.location.pathname, window.location.search);

      if (shouldAttemptSilentAuth) {
        sessionStorage.setItem(SILENT_AUTH_ATTEMPTED_KEY, '1');
        sessionStorage.setItem(SILENT_AUTH_RETURN_TO_KEY, window.location.toString());
        loginWithRedirect({ ...loginParams, prompt: 'none' });

        return;
      }

      login(loginParams);
    }
  }, [isAuthenticated, isLoading]);

  if (isRedirecting || isLoading || !isAuthenticated || !user) {
    return <AbsoluteSpinner />;
  }

  return <>{children}</>;
};
